import React, { useState, useEffect } from 'react'
import '../styles/dashboard.css'

// Mock data generator (same as before)
function generateMockData() {
  const wallets = [
    '9xQeWvG816bUx9EPjHmaT23yvVM2ZWbrrpZb9PusVFin',
    'DYw8jCTfwHNRJhhmFcbXvVDTqWMEVFBX6ZKUmG5CNSKK',
    '7wBqLJvGCqWMqV5AgZ9gY3qvHt8xJCqQnKzpZvCsyxj3'
  ]

  const domains = ['alice.sol', 'bob.sol', 'charlie.sol', 'dao.sol', null]
  const types = ['genesis', 'airdrop', 'referral', 'staking']

  const claims = []
  for (let i = 1; i <= 100; i++) {
    claims.push({
      id: i,
      wallet_address: wallets[Math.floor(Math.random() * wallets.length)],
      domain_name: domains[Math.floor(Math.random() * domains.length)],
      claim_type: types[Math.floor(Math.random() * types.length)],
      epoch: Math.floor(Math.random() * 3) + 1,
      amount: (Math.random() * 1000).toFixed(2),
      settled: Math.random() > 0.3,
      merkle_verified: Math.random() > 0.2,
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
    })
  }

  return {
    claims,
    stats: {
      total_claims: claims.length,
      total_settled: claims.filter(c => c.settled).length,
      total_pending: claims.filter(c => !c.settled).length,
      total_cred_distributed: claims.reduce((sum, c) => sum + parseFloat(c.amount), 0).toFixed(2)
    },
    snapshots: [
      { id: 1, epoch: 1, merkle_root: '0x1234...', claim_count: 50, finalized: true, created_at: new Date().toISOString() },
      { id: 2, epoch: 2, merkle_root: '0x5678...', claim_count: 30, finalized: true, created_at: new Date().toISOString() },
      { id: 3, epoch: 3, merkle_root: '0xabcd...', claim_count: 20, finalized: false, created_at: new Date().toISOString() }
    ],
    domains: [
      { domain_name: 'alice.sol', total_claims: 25, settled_claims: 20, pending_claims: 5, total_amount: '5000', settled_amount: '4000', pending_amount: '1000' },
      { domain_name: 'bob.sol', total_claims: 30, settled_claims: 25, pending_claims: 5, total_amount: '6000', settled_amount: '5000', pending_amount: '1000' }
    ]
  }
}

function DemoDashboard() {
  const [data, setData] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState('claims')

  useEffect(() => {
    // Simulate API loading
    setTimeout(() => {
      setData(generateMockData())
    }, 500)
  }, [])

  const filteredClaims = data?.claims.filter(c =>
    c.wallet_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.domain_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  const claimTypeIcons = {
    genesis: '👑',
    airdrop: '🎁',
    referral: '🤝',
    staking: '💎'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString()
  }

  if (!data) {
    return (
      <div className="dashboard">
        <div className="loading">Loading demo data...</div>
      </div>
    )
  }

  return (
    <div className="dashboard">
      {/* Demo Banner */}
      <div style={{
        background: '#fef3c7',
        border: '2px solid #f59e0b',
        padding: '12px 24px',
        borderRadius: '8px',
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        ⚠️ <strong>DEMO MODE</strong> - This is showing mock data. Visit <strong>/dashboard</strong> for live data.
      </div>

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>🎁 Claims Dashboard (Demo)</h1>
          <p>Exploring the interface with sample data</p>
        </div>
      </div>

      {/* Stats Cards */}
      {data.stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Claims</div>
            <div className="stat-value">{data.stats.total_claims}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Settled</div>
            <div className="stat-value success">{data.stats.total_settled}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pending</div>
            <div className="stat-value warning">{data.stats.total_pending}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total CRED</div>
            <div className="stat-value">{parseFloat(data.stats.total_cred_distributed).toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="view-tabs">
        <button
          className={viewMode === 'claims' ? 'tab active' : 'tab'}
          onClick={() => setViewMode('claims')}
        >
          📋 Claims
        </button>
        <button
          className={viewMode === 'snapshots' ? 'tab active' : 'tab'}
          onClick={() => setViewMode('snapshots')}
        >
          📸 Snapshots
        </button>
        <button
          className={viewMode === 'domains' ? 'tab active' : 'tab'}
          onClick={() => setViewMode('domains')}
        >
          🌐 Domains
        </button>
      </div>

      {/* Claims View */}
      {viewMode === 'claims' && (
        <div className="claims-view">
          <div className="filters-section">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Search by wallet or domain..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="results-info">
              Showing {filteredClaims.length} claims
            </div>
          </div>

          <div className="table-container">
            <table className="claims-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Wallet</th>
                  <th>Domain</th>
                  <th>Type</th>
                  <th>Epoch</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.slice(0, 20).map(claim => (
                  <tr key={claim.id}>
                    <td>{claim.id}</td>
                    <td className="mono">{claim.wallet_address.slice(0, 8)}...</td>
                    <td>{claim.domain_name || 'N/A'}</td>
                    <td>
                      <span className="type-badge">
                        {claimTypeIcons[claim.claim_type]} {claim.claim_type}
                      </span>
                    </td>
                    <td>{claim.epoch}</td>
                    <td className="amount">{parseFloat(claim.amount).toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${claim.settled ? 'settled' : 'pending'}`}>
                        {claim.settled ? '✅ Settled' : '⏳ Pending'}
                      </span>
                    </td>
                    <td className="date">{formatDate(claim.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Snapshots View */}
      {viewMode === 'snapshots' && (
        <div className="snapshots-view">
          <div className="table-container">
            <table className="claims-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Epoch</th>
                  <th>Root Hash</th>
                  <th>Claims</th>
                  <th>Finalized</th>
                </tr>
              </thead>
              <tbody>
                {data.snapshots.map(snapshot => (
                  <tr key={snapshot.id}>
                    <td>{snapshot.id}</td>
                    <td>{snapshot.epoch}</td>
                    <td className="mono">{snapshot.merkle_root}</td>
                    <td>{snapshot.claim_count}</td>
                    <td>
                      <span className={`status-badge ${snapshot.finalized ? 'settled' : 'pending'}`}>
                        {snapshot.finalized ? '✅ Yes' : '⏳ No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Domains View */}
      {viewMode === 'domains' && (
        <div className="domains-view">
          <div className="table-container">
            <table className="claims-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Total Claims</th>
                  <th>Settled</th>
                  <th>Pending</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.domains.map((domain, idx) => (
                  <tr key={idx}>
                    <td><strong>{domain.domain_name}</strong></td>
                    <td>{domain.total_claims}</td>
                    <td className="success">{domain.settled_claims}</td>
                    <td className="warning">{domain.pending_claims}</td>
                    <td className="amount">{parseFloat(domain.total_amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default DemoDashboard
